import { useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GovChangeRequest, GovItemDetail, GovSettingsOn } from "../../../../utils/icGovernance";
import { useGovernance } from "../GovernanceLayout";
import {
  DecisionChip,
  PriorityChip,
  SignOffChip,
  clientLabel,
  hours,
  itemUrl,
  linkedIds,
  vendorLabel,
} from "../governanceStatus";
import NewChangeRequestDialog from "../dialogs/NewChangeRequestDialog";
import { NewButton } from "../dialogs/dialogParts";
import { IdLink, LinkedIdList, LongText, Muted, ReleaseLink, PlainText, TitleLink, localDay } from "./cells";
import RegisterTable, { RegisterColumn, WaitingNotice } from "./RegisterTable";
import { useRegisterItems } from "./useRegisterItems";

const DecisionCell = ({ row }: { row: GovChangeRequest }) => {
  if (row.decision?.outcome) return <DecisionChip outcome={row.decision.outcome} />;
  if (row.status === "PENDING_DECISION") return <DecisionChip outcome={null} />;
  return <Muted />;
};

// Change Request — columns in the client's workbook order.
const changeRequestColumns = (s: GovSettingsOn): RegisterColumn<GovChangeRequest>[] => [
  { key: "id", header: "CR ID", render: (r) => <IdLink item={r} /> },
  { key: "requestedAt", header: "Date Requested", render: (r) => localDay(r.requestedAt || r.createdAt) },
  { key: "requestedBy", header: "Requested By", render: (r) => <PlainText value={r.requestedBy || r.createdBy} /> },
  {
    key: "title",
    header: "Change Title & Description",
    wrap: true,
    minWidth: 280,
    render: (r) => (
      <Stack spacing={0.5}>
        <TitleLink item={r} />
        {r.description ? (
          <Box sx={{ color: "#5a5a66", fontSize: 13 }}>
            <LongText value={r.description} lines={3} />
          </Box>
        ) : null}
      </Stack>
    ),
  },
  {
    key: "detailedRequest",
    header: "Detailed Request",
    wrap: true,
    hideByDefault: true,
    render: (r) => <LongText value={r.detailedRequest} />,
  },
  { key: "businessReason", header: "Business Reason", wrap: true, render: (r) => <LongText value={r.businessReason} /> },
  { key: "priority", header: "Priority", render: (r) => <PriorityChip priority={r.priority} /> },
  {
    key: "impact",
    header: `${vendorLabel(s)} Impact Assessment`,
    wrap: true,
    render: (r) => <LongText value={r.impactAssessment} />,
  },
  { key: "estHours", header: "Est. Hours", align: "right", render: (r) => hours(r.estimatedMinutes) },
  { key: "scheduleImpact", header: "Schedule Impact", wrap: true, minWidth: 180, render: (r) => <LongText value={r.scheduleImpact} /> },
  {
    key: "related",
    header: "Related Task / Issue",
    render: (r) => <LinkedIdList ids={linkedIds(r, ["TASK", "ISSUE"])} />,
  },
  { key: "decision", header: `${clientLabel(s)} Decision`, render: (r) => <DecisionCell row={r} /> },
  { key: "decisionBy", header: "Decision By", render: (r) => <PlainText value={r.decision?.by} /> },
  { key: "decisionDate", header: "Decision Date", render: (r) => localDay(r.decision?.at) },
  { key: "targetRelease", header: "Target Release", render: (r) => <ReleaseLink release={r.targetRelease} /> },
  { key: "signOffBy", header: `${clientLabel(s)} Sign-Off By`, render: (r) => <PlainText value={r.signOff?.decidedBy} /> },
  {
    key: "signOffDate",
    header: "Sign-Off Date",
    render: (r) => (
      <Stack direction="row" spacing={1} alignItems="center">
        <span>{localDay(r.signOff?.decidedAt)}</span>
        <SignOffChip status={r.signOff?.status} />
      </Stack>
    ),
  },
];

const WAITING: WaitingNotice<GovChangeRequest> = {
  reason: (r) => (r.status === "PENDING_DECISION" ? "decision" : null),
  describe: (n) => `${n} change ${n === 1 ? "request waits" : "requests wait"} for your decision`,
};

const ChangeRequestRegister = () => {
  const { settings } = useGovernance();
  const navigate = useNavigate();
  const register = useRegisterItems<GovChangeRequest>("CHANGE_REQUEST");
  const columns = useMemo(() => changeRequestColumns(settings), [settings]);
  const [newOpen, setNewOpen] = useState(false);

  const onCreated = (item: GovItemDetail) => {
    setNewOpen(false);
    navigate(itemUrl(item));
  };

  return (
    <>
      <RegisterTable
        {...register}
        kind="CHANGE_REQUEST"
        columns={columns}
        noun={{ one: "change request", many: "change requests" }}
        caption={`${clientLabel(settings)} requests and decides; ${vendorLabel(settings)} assesses`}
        actions={<NewButton label="New change request" onClick={() => setNewOpen(true)} />}
        waiting={WAITING}
      />
      <NewChangeRequestDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={onCreated} />
    </>
  );
};

export default ChangeRequestRegister;
